import * as T from 'three';
import type { RideTrack } from '../model';
import { envLibraryLoading, type EnvLibrary } from './env-assets';
import { makeForest, type TreeInstance } from './impostor';
import type { Quality } from './quality';
import { makeNoise } from './noise';

/**
 * Trees, boulders, logs and ground plants. Photographic assets from the environment library
 * when present; otherwise simple procedural stand-ins so the game still runs offline.
 */
export interface PropContext {
  track: RideTrack; quality: Quality; rng: () => number;
  sample: (u: number) => { point: T.Vector3; right: T.Vector3 };
  height: (x: number, z: number) => number;
  reserved: (x: number, z: number) => boolean;
  maxX: number; waterLevel: number | null;
}
/** Scale ranges turn the scanned specimens into mature trees of plausible size for each arena. */
interface Species { id: string; weight: number; scale: [number, number] }
const TREES: Record<RideTrack['biome'], { count: number; range: number; species: Species[] }> = {
  alpine: { count: 2600, range: 200, species: [{ id: 'fir_tree_01', weight: .9, scale: [.62, 1.18] }, { id: 'tree_small_02', weight: .1, scale: [1.4, 2] }] },
  canyon: { count: 220, range: 170, species: [{ id: 'searsia_lucida', weight: 1, scale: [.9, 1.7] }] },
  coast: { count: 420, range: 110, species: [{ id: 'palm', weight: .45, scale: [.7, 1.05] }, { id: 'island_tree_02', weight: .55, scale: [1.5, 2.3] }] },
  meadow: { count: 190, range: 170, species: [{ id: 'tree_small_02', weight: .75, scale: [1.6, 2.3] }, { id: 'island_tree_02', weight: .25, scale: [1.8, 2.6] }] },
  harbour: { count: 130, range: 120, species: [{ id: 'palm', weight: .5, scale: [.7, 1] }, { id: 'island_tree_02', weight: .5, scale: [1.6, 2.4] }] },
  park: { count: 130, range: 150, species: [{ id: 'jacaranda_tree', weight: .55, scale: [.5, .72] }, { id: 'tree_small_02', weight: .45, scale: [1.6, 2.2] }] },
};
const ROCKS: Record<RideTrack['biome'], { ids: string[]; count: number; tint: string }> = {
  alpine: { ids: ['rock_moss_set_01_a', 'rock_moss_set_01_b', 'boulder_01'], count: 460, tint: '#ffffff' },
  canyon: { ids: ['namaqualand_boulder_02', 'boulder_01'], count: 420, tint: '#f2b08e' },
  coast: { ids: ['coast_rocks_05', 'boulder_01'], count: 220, tint: '#f4eee2' },
  meadow: { ids: ['boulder_01'], count: 70, tint: '#ffffff' },
  harbour: { ids: ['coast_rocks_05'], count: 60, tint: '#ffffff' },
  park: { ids: ['boulder_01'], count: 60, tint: '#fff0f4' },
};
const PLANTS: Record<RideTrack['biome'], { id: string; count: number; near: number; far: number; scale: [number, number]; view: number }[]> = {
  alpine: [{ id: 'fern_02', count: 520, near: 1.5, far: 26, scale: [.7, 1.3], view: 110 }, { id: 'dead_tree_trunk', count: 60, near: 4, far: 60, scale: [.8, 1.5], view: 260 }],
  canyon: [{ id: 'wild_rooibos_bush', count: 340, near: 2, far: 40, scale: [.6, 1.4], view: 160 }],
  coast: [{ id: 'wild_rooibos_bush', count: 260, near: 2, far: 30, scale: [.6, 1.2], view: 140 }, { id: 'shrub_02', count: 90, near: 3, far: 45, scale: [.6, 1.1], view: 170 }],
  meadow: [{ id: 'flower_gazania', count: 1300, near: 1.2, far: 26, scale: [.8, 1.4], view: 70 }, { id: 'dandelion_01', count: 900, near: 1.2, far: 22, scale: [.8, 1.3], view: 60 }, { id: 'shrub_02', count: 70, near: 4, far: 50, scale: [.7, 1.2], view: 180 }],
  harbour: [{ id: 'shrub_02', count: 120, near: 2, far: 22, scale: [.6, 1], view: 120 }, { id: 'flower_gazania', count: 300, near: 1.2, far: 14, scale: [.8, 1.2], view: 60 }],
  park: [{ id: 'shrub_02', count: 120, near: 3, far: 35, scale: [.7, 1.2], view: 160 }, { id: 'flower_gazania', count: 500, near: 1.2, far: 18, scale: [.8, 1.3], view: 60 }],
};

interface Chunk { mesh: T.InstancedMesh; x: number; z: number; view: number }
export class Props {
  group = new T.Group(); occluders: { x: number; z: number; r: number }[] = []; time = { value: 0 };
  private chunks: Chunk[] = [];
  constructor(private ctx: PropContext, private lib: EnvLibrary | null) {
    this.group.name = 'props';
    this.trees(); this.rocks(); this.plants();
  }
  private pick(species: Species[], r: number) { let acc = 0; for (const s of species) { acc += s.weight; if (r <= acc) return s; } return species[species.length - 1]; }
  private impostor(id: string) {
    if (!this.lib) return null;
    if (this.lib.impostors.has(id)) return this.lib.impostors.get(id)!;
    for (const [key, spec] of this.lib.impostors) if (id === 'palm' && key.includes('palm')) return spec;
    return null;
  }
  private trees() {
    const { track, rng, quality } = this.ctx, cfg = TREES[track.biome], count = Math.round(cfg.count * quality.trees);
    const hw = track.width / 2, byType = new Map<string, TreeInstance[]>(), fallback: TreeInstance[] = [], stands = makeNoise(track.seed + 5);
    // Trees grow in stands: candidates in thin noise areas are skipped, so forests have glades.
    for (let i = 0, tries = 0; i < count && tries < count * 3; tries++) {
      const species = this.pick(cfg.species, rng()), spec = this.impostor(species.id);
      const scale = spec ? species.scale[0] + rng() * (species.scale[1] - species.scale[0]) : .72 + rng() * .55, canopy = spec ? spec.width * scale * .42 : 3;
      const u = rng(), { point, right } = this.ctx.sample(u), side = rng() < .5 ? -1 : 1;
      point.addScaledVector(right, side * (hw + Math.max(5, canopy + 1.5) + rng() ** 1.6 * cfg.range));
      if (track.biome === 'coast' && point.x > this.ctx.maxX + 5) point.x = this.ctx.maxX - 5 - rng() * 30;
      const yaw = rng() * Math.PI * 2;
      if (stands.fbm(point.x / 70, point.z / 70, 3) < .44 + rng() * .12) continue;
      i++;
      if (this.ctx.reserved(point.x, point.z)) continue;
      const y = this.ctx.height(point.x, point.z);
      if (this.ctx.waterLevel !== null && y < this.ctx.waterLevel + .5) continue;
      const tint = new T.Color().setHSL(.08 + rng() * .06, .1 + rng() * .15, .45 + rng() * .1).lerp(new T.Color(1, 1, 1), .78);
      const tree = { x: point.x, y: y - .15, z: point.z, scale, yaw, tint };
      if (spec) { const list = byType.get(spec.id) ?? []; list.push(tree); byType.set(spec.id, list); } else fallback.push(tree);
      this.occluders.push({ x: point.x, z: point.z, r: Math.max(1.5, canopy * .9) });
    }
    for (const [id, list] of byType) { const forest = makeForest(this.lib!.impostors.get(id)!, list, this.time); forest.castShadow = this.ctx.quality.level !== 'low'; this.group.add(forest); }
    if (fallback.length && !envLibraryLoading()) this.group.add(fallbackTrees(track, fallback));
  }
  private rocks() {
    const { track, rng } = this.ctx, cfg = ROCKS[track.biome], hw = track.width / 2, total = Math.round(cfg.count * (this.ctx.quality.level === 'low' ? .25 : 1));
    const assets = cfg.ids.map(id => this.lib?.meshes.get(id)).filter(Boolean) as NonNullable<ReturnType<EnvLibrary['meshes']['get']>>[];
    const tint = new T.Color(cfg.tint), dummy = new T.Object3D();
    // Two buckets per rock type: full detail near the trail, simplified further out.
    if (!assets.length && envLibraryLoading()) return;
    const lists: T.Matrix4[][] = (assets.length ? assets : [null]).flatMap(() => [[], []]);
    const low = this.ctx.quality.level === 'low';
    for (let i = 0; i < total; i++) {
      const { point, right } = this.ctx.sample(rng()), far = rng() < .25;
      point.addScaledVector(right, (rng() < .5 ? -1 : 1) * (hw + 1.2 + (far ? 20 + rng() * 120 : rng() * 18)));
      if (this.ctx.reserved(point.x, point.z)) continue;
      const size = far ? 1.6 + rng() * (track.biome === 'canyon' ? 7 : 4) : .35 + rng() ** 2 * (track.biome === 'canyon' ? 2.6 : 1.8);
      const y = this.ctx.height(point.x, point.z);
      dummy.position.set(point.x, y - size * .12, point.z); dummy.rotation.set((rng() - .5) * .3, rng() * Math.PI * 2, (rng() - .5) * .3); dummy.scale.set(size, size * (.8 + rng() * .5), size); dummy.updateMatrix();
      lists[Math.floor(rng() * (lists.length / 2)) * 2 + (far || low ? 1 : 0)].push(dummy.matrix.clone());
      if (size > 1.4) this.occluders.push({ x: point.x, z: point.z, r: size * .8 });
    }
    lists.forEach((matrices, k) => {
      if (!matrices.length) return;
      const asset = assets[k >> 1];
      const geometry = asset ? (k % 2 ? asset.lod : asset.geometry) : rockGeometry(7 + k), material = asset ? asset.material : new T.MeshStandardMaterial({ color: track.biome === 'canyon' ? '#a8664a' : '#8b8f88', roughness: .9 });
      const mesh = new T.InstancedMesh(geometry, material, matrices.length); mesh.name = `rocks-${asset?.id ?? 'procedural'}`;
      matrices.forEach((m, i) => { mesh.setMatrixAt(i, m); mesh.setColorAt(i, tint.clone().multiplyScalar(.85 + Math.random() * .25)); });
      mesh.instanceMatrix.needsUpdate = true; mesh.castShadow = this.ctx.quality.level !== 'low'; mesh.receiveShadow = true; mesh.computeBoundingSphere(); this.group.add(mesh);
    });
  }
  private plants() {
    const { track, rng } = this.ctx, hw = track.width / 2, chunkLen = 120;
    for (const cfg of PLANTS[track.biome]) {
      const asset = this.lib?.meshes.get(cfg.id); if (!asset || this.ctx.quality.level === 'low') continue;
      const count = Math.round(cfg.count * Math.max(.35, this.ctx.quality.grass)), perChunk = new Map<number, T.Matrix4[]>(), dummy = new T.Object3D();
      for (let i = 0; i < count; i++) {
        const u = rng(), { point, right } = this.ctx.sample(u);
        point.addScaledVector(right, (rng() < .5 ? -1 : 1) * (hw + cfg.near + rng() ** 1.4 * (cfg.far - cfg.near)));
        if (this.ctx.reserved(point.x, point.z)) continue;
        const y = this.ctx.height(point.x, point.z);
        if (this.ctx.waterLevel !== null && y < this.ctx.waterLevel + .3) continue;
        const s = cfg.scale[0] + rng() * (cfg.scale[1] - cfg.scale[0]);
        dummy.position.set(point.x, y - .03, point.z); dummy.rotation.set(0, rng() * Math.PI * 2, 0); dummy.scale.setScalar(s); dummy.updateMatrix();
        const key = Math.floor(u * track.length / chunkLen); const list = perChunk.get(key) ?? []; list.push(dummy.matrix.clone()); perChunk.set(key, list);
      }
      for (const [key, matrices] of perChunk) {
        const mesh = new T.InstancedMesh(asset.geometry, asset.material, matrices.length); mesh.name = `plants-${cfg.id}`;
        matrices.forEach((m, i) => mesh.setMatrixAt(i, m)); mesh.instanceMatrix.needsUpdate = true; mesh.receiveShadow = true; mesh.castShadow = cfg.id === 'dead_tree_trunk'; mesh.computeBoundingSphere();
        const c = this.ctx.sample(Math.min(1, (key + .5) * chunkLen / track.length)).point;
        this.chunks.push({ mesh, x: c.x, z: c.z, view: cfg.view + chunkLen * .6 }); this.group.add(mesh);
      }
    }
  }
  update(time: number, camera: T.Vector3) {
    this.time.value = time;
    for (const c of this.chunks) c.mesh.visible = Math.hypot(c.x - camera.x, c.z - camera.z) < c.view;
  }
  dispose() {
    this.group.traverse(o => { if (o instanceof T.InstancedMesh) { const own = !this.lib || ![...this.lib.meshes.values()].some(a => a.geometry === o.geometry || a.lod === o.geometry); if (own) { o.geometry.dispose(); (o.material as T.Material).dispose(); } o.dispose(); } });
  }
}

/** Lumpy, smooth-shaded boulder: noise-displaced icosphere. */
function rockGeometry(seed: number) {
  const geo = new T.IcosahedronGeometry(.5, 3), p = geo.attributes.position, v = new T.Vector3();
  for (let i = 0; i < p.count; i++) {
    v.fromBufferAttribute(p, i); const n = Math.sin(v.x * 7 + seed) * Math.cos(v.z * 6 - seed) * .08 + Math.sin(v.y * 11 + seed * 2) * .04;
    v.multiplyScalar(1 + n); v.y = Math.max(v.y * .75, -.1) + .1; p.setXYZ(i, v.x, v.y, v.z);
  }
  geo.computeVertexNormals(); return geo;
}
/** Offline stand-in trees: smooth cones and lobes, instanced. */
function fallbackTrees(track: RideTrack, trees: TreeInstance[]) {
  const group = new T.Group(), conifer = track.biome === 'alpine';
  const crown = new T.InstancedMesh(conifer ? new T.ConeGeometry(1, 1, 12) : new T.IcosahedronGeometry(1, 2), new T.MeshStandardMaterial({ color: conifer ? '#2f5a3e' : '#58783e', roughness: .9 }), trees.length);
  const trunk = new T.InstancedMesh(new T.CylinderGeometry(.12, .22, 1, 8), new T.MeshStandardMaterial({ color: '#5d4d3c', roughness: .95 }), trees.length);
  const m = new T.Object3D();
  trees.forEach((t, i) => {
    const h = (conifer ? 16 : 8) * t.scale;
    m.position.set(t.x, t.y + h * .25, t.z); m.rotation.set(0, t.yaw, 0); m.scale.set(1, h * .5, 1); m.updateMatrix(); trunk.setMatrixAt(i, m.matrix);
    m.position.set(t.x, t.y + h * (conifer ? .58 : .72), t.z); m.scale.set(h * (conifer ? .2 : .3), h * (conifer ? .8 : .32), h * (conifer ? .2 : .3)); m.updateMatrix(); crown.setMatrixAt(i, m.matrix);
  });
  for (const mesh of [crown, trunk]) { mesh.castShadow = true; mesh.receiveShadow = true; mesh.computeBoundingSphere(); group.add(mesh); }
  return group;
}
